<template>
  <div id="bubblesContainer" ref="bubblesContainer" class="h-100 mh-100 bubbles-container">
    <BubblesCanvas />
    <div data-test="data-visualisation-legends" class="legends-container position-legends-top-left">
      <FilterBubblesUI v-if="filterByCategoryPosition === TOP_LEFT" />
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition === TOP_LEFT" />
    </div>
    <div
      data-test="data-visualisation-legends"
      class="legends-container position-legends-bottom-left"
    >
      <FilterBubblesUI v-if="filterByCategoryPosition === BOTTOM_LEFT" />
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition === BOTTOM_LEFT" />
    </div>
    <div
      data-test="data-visualisation-legends"
      class="legends-container position-legends-bottom-right"
    >
      <FilterBubblesUI v-if="filterByCategoryPosition === BOTTOM_RIGHT" />
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition === BOTTOM_RIGHT" />
    </div>
    <BubblePopup v-if="showPopup" />
    <div id="bubblesMinSizeSquare" ref="bubblesMinSizeSquare" :style="bubblesMinSizeSquareStyle" />
    <BubblesHandles />
    <BubblesHeaders />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import BubblesCanvas from '@/components/public/Visualisations/Bubbles/BubblesCanvas.vue'
import BubblesHandles from '@/components/public/Visualisations/Bubbles/Handles/BubblesHandles.vue'
import BubblesHeaders from '@/components/public/Visualisations/Bubbles/Headers/BubblesHeaders.vue'
import BubblePopup from '@/components/public/Popup/BubblePopup.vue'
import FilterBubblesUI from '@/components/public/FilterByCategory/FilterBubblesUI.vue'
import BubblesLegendsAccordion from '@/components/public/BubblesLegendsAccordion.vue'

import { LegendPositions } from '@/types'

const components = {
  BubblesCanvas,
  BubblePopup,
  BubblesHandles,
  BubblesHeaders,
  FilterBubblesUI,
  BubblesLegendsAccordion,
}

@Component({ components })
export default class Bubbles extends Vue {
  TOP_LEFT = LegendPositions.TOP_LEFT
  BOTTOM_LEFT = LegendPositions.BOTTOM_LEFT
  BOTTOM_RIGHT = LegendPositions.BOTTOM_RIGHT

  resizeObserver: ResizeObserver | null = null

  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeBubblesMinSizeSquare()
      this.$typedStore.public.size.updateBubblesContainerDimensions()
      this.$typedStore.public.size.updateBubblesMinSizeSquareOffset()
      this.$typedStore.public.size.updateBubblesVisualisationScaleFactor()
    })
    this.resizeObserver.observe(this.$el as HTMLElement)
  }

  beforeDestroy() {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  resizeBubblesMinSizeSquare() {
    const size = Math.min(this.bubblesContainer.clientWidth, this.bubblesContainer.clientHeight)
    this.bubblesMinSizeSquare.style.width = `${size}px`
    this.bubblesMinSizeSquare.style.height = `${size}px`
  }

  get bubblesContainer() {
    return this.$refs.bubblesContainer! as HTMLDivElement
  }

  get bubblesMinSizeSquare() {
    return this.$refs.bubblesMinSizeSquare! as HTMLDivElement
  }

  get editMode() {
    return this.$typedStore.public.display.showHandles
  }

  get showPopup() {
    return !this.editMode
  }

  get bubblesMinSizeSquareStyle() {
    const opacity = this.editMode ? 1 : 0
    return {
      opacity,
    }
  }

  get filterByCategoryPosition() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.filterByCategory
      .position
  }

  get bubblesLegendsPosition() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.bubblesLegends
      .legendPosition
  }
}
</script>

<style lang="scss" scoped>
.bubbles-container {
  overflow: hidden;
  max-width: 100%;
}

#bubblesMinSizeSquare {
  background-color: rgba(0, 0, 0, 0);
  border: 1px solid black;
  pointer-events: none;

  /* Center the div */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.position-legends-top-left {
  position: absolute;
  top: 0;
  left: 0;
  margin-top: 0.5rem;
  margin-left: 18px;
}

.position-legends-bottom-left {
  position: absolute;
  bottom: 0;
  left: 0;
  margin-bottom: 0.5rem;
  margin-left: 18px;
}

.position-legends-bottom-right {
  position: absolute;
  bottom: 0;
  right: 0;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
}

.legends-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1;
}
</style>
